import axios from 'axios'
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  ListGroup,
  ListGroupItem,
} from 'reactstrap'
import Swal from 'sweetalert2'

const fetchManagers = async () => {
  const res = await axios.get('manager-feedback-surveys/forward-candidates')
  return res.data.data || []
}

const ForwardSurveyModal = ({ surveyId, currentManagerId, isOpen, onClose, onForwarded }) => {
  const queryClient = useQueryClient()
  const [selectedManagerId, setSelectedManagerId] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const { data: managers = [], isLoading } = useQuery({
    queryKey: ['forwardable-managers'],
    queryFn: fetchManagers,
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  })

  const filteredManagers = useMemo(() => {
    const candidates = managers.filter((manager) => manager._id !== currentManagerId)
    const term = searchTerm.trim().toLowerCase()
    if (!term) return candidates
    return candidates.filter((manager) =>
      `${manager.first_name} ${manager.last_name}`.toLowerCase().includes(term),
    )
  }, [managers, currentManagerId, searchTerm])

  const forwardMutation = useMutation({
    mutationFn: (newManagerId) =>
      axios.patch(`manager-feedback-surveys/${surveyId}/forward`, { newManagerId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manager-surveys'] })
      Swal.fire('Sukses', 'Vlerësimi u përcoll me sukses.', 'success')
      setSelectedManagerId('')
      setSearchTerm('')
      onForwarded?.(surveyId)
      onClose()
    },
    onError: (err) => {
      Swal.fire('Gabim', err.response?.data?.message || 'Gabim gjatë përcjelljes.', 'error')
    },
  })

  const handleClose = () => {
    setSelectedManagerId('')
    setSearchTerm('')
    onClose()
  }

  const handleSubmit = () => {
    if (selectedManagerId) forwardMutation.mutate(selectedManagerId)
  }

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size='lg' className='forward-survey-modal'>
      <style>{`
        .forward-survey-modal { margin-top: 10vh; }
        .forward-survey-modal .modal-content { min-height: 420px; }
      `}</style>
      <ModalHeader toggle={handleClose}>Përcille vlerësimin</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label>Kërko menaxherin te i cili do ta përcjellësh:</Label>
          <Input
            type='text'
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder='Shkruani emrin...'
            autoFocus
          />
        </FormGroup>

        <ListGroup style={{ maxHeight: 260, overflowY: 'auto' }}>
          {isLoading && <ListGroupItem disabled>Duke ngarkuar...</ListGroupItem>}
          {!isLoading && filteredManagers.length === 0 && (
            <ListGroupItem disabled>Nuk u gjet asnjë menaxher</ListGroupItem>
          )}
          {!isLoading &&
            filteredManagers.map((manager) => {
              const isSelected = selectedManagerId === manager._id
              return (
                <ListGroupItem
                  key={manager._id}
                  tag='button'
                  type='button'
                  action
                  onClick={() => setSelectedManagerId(manager._id)}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: isSelected ? '#f1f3f5' : undefined,
                    color: '#212529',
                  }}
                >
                  <span>
                    {manager.first_name} {manager.last_name}
                  </span>
                  {isSelected && (
                    <span style={{ color: '#6c757d', fontSize: '1rem', lineHeight: 1 }}>&#10003;</span>
                  )}
                </ListGroupItem>
              )
            })}
        </ListGroup>
      </ModalBody>
      <ModalFooter>
        <Button color='secondary' onClick={handleClose}>
          Anulo
        </Button>
        <Button
          color='primary'
          onClick={handleSubmit}
          disabled={!selectedManagerId || forwardMutation.isLoading}
        >
          {forwardMutation.isLoading ? 'Duke dërguar...' : 'Përcille'}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default ForwardSurveyModal
